import { useEventListener, useInterval, useMemoizedFn, useMount } from "ahooks";
import { duration } from "common";
import { useRef, useState } from "react";
import { markActivity, readIdleLimit, readLastActivity, setSellerIdleLimit } from "@/auth/idleSession";
import { signOut, SignOutReason } from "@/auth/signOut";
import { log } from "@/utils/log";

const TICK_MS = duration(5, "seconds");
const WARNING_MS = duration(60, "seconds");

export const useAutoLogout = (sellerId: string, sellerIdleMinutes: number | null, isImpersonationTab = false) => {
    const [warningDeadline, setWarningDeadline] = useState<number | null>(null);
    // Impersonation tabs keep their own clock and limit, so they never keep other tabs alive or cut them short.
    const tabLastActivityAt = useRef(Date.now());
    const hasSignedOut = useRef(false);

    const getLimitMinutes = () => (isImpersonationTab ? sellerIdleMinutes : readIdleLimit());

    const getIdleSignOutAt = () => {
        const limitMinutes = getLimitMinutes();
        const lastActivityAt = isImpersonationTab ? tabLastActivityAt.current : readLastActivity();
        return !limitMinutes || lastActivityAt === null ? null : lastActivityAt + duration(limitMinutes, "minutes");
    };

    // Refreshed before every check so a changed setting or a wake from sleep never reads a stale limit.
    const refreshSellerLimit = () => {
        if (!isImpersonationTab) {
            setSellerIdleLimit(sellerId, sellerIdleMinutes);
        }
    };

    const recordActivity = () => {
        if (isImpersonationTab) {
            tabLastActivityAt.current = Date.now();
        } else {
            markActivity();
        }
    };

    const signOutIdle = () => {
        if (hasSignedOut.current) {
            return;
        }
        hasSignedOut.current = true;
        setWarningDeadline(null);
        log("Signed out after inactivity", { idleMinutes: getLimitMinutes() }, true);
        signOut(SignOutReason.IDLE);
    };

    // Every tick re-reads shared storage, so other tabs, sleep and reload need no extra code.
    const checkIdle = () => {
        if (hasSignedOut.current) {
            return;
        }
        refreshSellerLimit();

        const signOutAt = getIdleSignOutAt();
        if (signOutAt === null) {
            setWarningDeadline(null);
            return;
        }

        const remainingMs = signOutAt - Date.now();
        if (remainingMs <= 0) {
            signOutIdle();
            return;
        }

        setWarningDeadline(remainingMs <= WARNING_MS ? signOutAt : null);
    };

    const handleUserInput = (event: Event) => {
        if (!event.isTrusted) {
            return;
        }

        // A click after the deadline must not revive the session; background tabs can run their check late.
        refreshSellerLimit();
        const signOutAt = getIdleSignOutAt();
        if (signOutAt !== null && signOutAt <= Date.now()) {
            signOutIdle();
            return;
        }

        // Once warned, only "Stay Logged In" extends the session, not a stray click on the modal or backdrop.
        if (warningDeadline !== null) {
            return;
        }

        recordActivity();
    };

    useMount(() => {
        if (!isImpersonationTab && readLastActivity() === null) {
            markActivity();
        }
    });

    useInterval(checkIdle, TICK_MS, { immediate: true });
    // Capture phase so a component calling stopPropagation cannot hide the input.
    useEventListener("pointerdown", handleUserInput, { target: window, capture: true });
    useEventListener("keydown", handleUserInput, { target: window, capture: true });

    const dismissWarning = useMemoizedFn(() => {
        recordActivity();
        setWarningDeadline(null);
    });

    return { warningDeadline, dismissWarning };
};
